import { useQueryClient } from "@tanstack/react-query";
import { doc, getFirestore, runTransaction } from "firebase/firestore";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useClientesActions, useRutasActions } from "../../../context/AppContext";
import { queryKeys } from "../../../queries/queryKeys";
import {
  aplicarMovimientoARutaLocal,
  construirActualizacionRutaConMovimiento,
  crearMovimientoRutaData,
  crearMovimientoRutaRef,
} from "../../../utils/rutaMovimientos";
import type { ModalDeleteClienteProps, UseModalDeleteClienteReturn } from "./types";

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

export const useModalDeleteCliente = ({
  selectedCliente,
  setClientes,
  setIsModalDelete,
  userData,
  usuarioRuta,
}: ModalDeleteClienteProps): UseModalDeleteClienteReturn | null => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { setRutasData } = useRutasActions();
  const { setInfoClientes } = useClientesActions();
  const queryClient = useQueryClient();
  const adminUid = userData?.uid ?? null;
  const rutaUid = usuarioRuta?.uid ?? null;
  const selectedClienteUid = selectedCliente?.uid ?? null;

  const handleClose = useCallback(() => {
    setIsModalDelete(false);
  }, [setIsModalDelete]);

  const deleteCliente = useCallback(async () => {
    if (isDeleting || !selectedClienteUid || !adminUid || !rutaUid || !selectedCliente) {
      return;
    }

    setIsDeleting(true);

    try {
      const db = getFirestore();
      const rutaRef = doc(db, "admin_users", adminUid, "rutas", rutaUid);
      const clienteRef = doc(
        db,
        "admin_users",
        adminUid,
        "rutas",
        rutaUid,
        "clientes",
        selectedClienteUid,
      );

      const debeActualizarRuta = normalizarNumero(selectedCliente.cuotasPagadas) === 0;
      const movimientoRef = debeActualizarRuta
        ? crearMovimientoRutaRef(db, adminUid, rutaUid)
        : null;
      const fechaMovimiento = new Date();
      const resultadoTransaccion = await runTransaction(db, async (transaction) => {
        let movimientoEliminacion: ReturnType<typeof crearMovimientoRutaData> | null = null;
        let saldoActualizado: number | null = null;

        if (debeActualizarRuta) {
          const rutaSnapshot = await transaction.get(rutaRef);
          const rutaData = rutaSnapshot.data();

          if (!rutaData) {
            throw new Error("No se pudo obtener la ruta actual.");
          }

          saldoActualizado =
            normalizarNumero(rutaData.saldoInicial) +
            normalizarNumero(selectedCliente.valorPrestamo);
          movimientoEliminacion = crearMovimientoRutaData({
            descripcion:
              normalizarNumero(selectedCliente.cuotasPagadas) === 0
                ? "Cliente eliminado S/C"
                : "Cliente eliminado",
            fecha: fechaMovimiento,
            monto: normalizarNumero(selectedCliente.valorPrestamo),
            origen: "eliminar_cliente",
            responsable: "Admin",
            saldoDespues: saldoActualizado,
          });
          const rutaUpdates = construirActualizacionRutaConMovimiento(
            rutaData,
            movimientoEliminacion,
            saldoActualizado,
          );

          if (movimientoRef) {
            transaction.set(movimientoRef, movimientoEliminacion);
          }

          transaction.update(rutaRef, rutaUpdates);
        }

        transaction.delete(clienteRef);

        return {
          movimientoEliminacion,
          saldoActualizado,
        };
      });

      if (
        resultadoTransaccion.movimientoEliminacion &&
        resultadoTransaccion.saldoActualizado !== null
      ) {
        setRutasData((prevRutas) =>
          prevRutas
            ? prevRutas.map((ruta) =>
                ruta.uid === rutaUid
                  ? aplicarMovimientoARutaLocal(
                      ruta,
                      resultadoTransaccion.movimientoEliminacion,
                      resultadoTransaccion.saldoActualizado,
                    )
                  : ruta,
              )
            : prevRutas,
        );
      }

      setClientes((prevClientes) =>
        prevClientes.filter((cliente) => cliente.uid !== selectedClienteUid),
      );

      // setClientes de arriba solo actualiza el estado local de la tabla;
      // patcheamos tambien la cache compartida de "clientes" para que otras
      // vistas de la misma ruta (o un remount de esta tabla) no vuelvan a
      // mostrar el cliente eliminado mientras el staleTime no haya expirado.
      setInfoClientes(
        (prevClientes) =>
          prevClientes?.filter((cliente) => cliente.uid !== selectedClienteUid) ?? prevClientes,
      );

      if (resultadoTransaccion.movimientoEliminacion) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientos(adminUid, rutaUid),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.movimientosDelDiaByRuta(adminUid, rutaUid),
        });
      }

      handleClose();
      toast.success("Cliente Eliminado con exito");
    } catch (error) {
      console.error("Error al eliminar cliente:", error);
      toast.error("No se pudo eliminar el cliente");
    } finally {
      setIsDeleting(false);
    }
  }, [
    adminUid,
    handleClose,
    isDeleting,
    queryClient,
    rutaUid,
    selectedCliente,
    selectedClienteUid,
    setClientes,
    setInfoClientes,
    setRutasData,
  ]);

  if (!selectedClienteUid) {
    return null;
  }

  return {
    deleteCliente,
    handleClose,
    isDeleting,
    selectedCliente,
  };
};
